import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

const ExperiencesPage = () => {
  const sections = [
    {
      id: 'nightlife',
      title: 'Nightlife & Social',
      subtitle: 'Where the city reveals itself',
      description: 'The best tables aren\'t listed. The right parties don\'t send invitations. We know the owners, the promoters, the people who decide who enters. Your name is already on the list.',
      experiences: [
        {
          title: 'Reserved Tables',
          description: 'Premium positions at venues that officially don\'t take reservations.',
          image: 'https://images.unsplash.com/photo-1470225620780-dba8ba36b745?auto=format&fit=crop&w=600&q=80'
        },
        {
          title: 'Rooftop Gatherings',
          description: 'Private access to the city\'s most coveted skyline views.',
          image: 'https://images.unsplash.com/photo-1566417713940-fe7c737a9ef2?auto=format&fit=crop&w=600&q=80'
        },
        {
          title: 'After Hours',
          description: 'When the public party ends, yours is just beginning.',
          image: 'https://images.unsplash.com/photo-1768610284978-8116a501bfe1?auto=format&fit=crop&w=600&q=80'
        }
      ],
      mainImage: 'https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?auto=format&fit=crop&w=1200&q=80'
    },
    {
      id: 'private',
      title: 'Private Experiences',
      subtitle: 'Your world, orchestrated',
      description: 'Some moments require complete privacy. A villa with no neighbors. A chef who cooks only for you. A helicopter waiting on your schedule. We arrange the invisible details that make the impossible feel effortless.',
      experiences: [
        {
          title: 'Private Residences',
          description: 'Staffed villas and penthouses in locations you won\'t find online.',
          image: 'https://images.unsplash.com/photo-1613490493576-7fde63acd811?auto=format&fit=crop&w=600&q=80'
        },
        {
          title: 'Personal Chefs',
          description: 'Michelin-trained talent creating menus around your preferences.',
          image: 'https://images.pexels.com/photos/28616663/pexels-photo-28616663.jpeg?auto=compress&w=600'
        },
        {
          title: 'Private Aviation',
          description: 'Helicopters and jets arranged without the usual complications.',
          image: 'https://images.unsplash.com/photo-1551636898-47668aa61de2?auto=format&fit=crop&w=600&q=80'
        }
      ],
      mainImage: 'https://images.unsplash.com/photo-1601913462945-776e38913868?auto=format&fit=crop&w=1200&q=80'
    },
    {
      id: 'events',
      title: 'Events & Celebrations',
      subtitle: 'Milestones that become legends',
      description: 'A birthday that redefines celebration. A bachelor weekend they\'ll talk about for decades. A proposal so perfect it feels like fate. We don\'t plan events — we create stories.',
      experiences: [
        {
          title: 'Milestone Celebrations',
          description: 'Birthdays and anniversaries designed around who you truly are.',
          image: 'https://images.unsplash.com/photo-1530103862676-de8c9debad1d?auto=format&fit=crop&w=600&q=80'
        },
        {
          title: 'Bachelor Experiences',
          description: 'The kind of weekend that bonds friends for life.',
          image: 'https://images.unsplash.com/photo-1532635241-17e820acc59f?auto=format&fit=crop&w=600&q=80'
        },
        {
          title: 'Private Occasions',
          description: 'Proposals, reunions, and moments that demand perfection.',
          image: 'https://images.unsplash.com/photo-1763888647863-d6d0c7383151?auto=format&fit=crop&w=600&q=80'
        }
      ],
      mainImage: 'https://images.unsplash.com/photo-1519671482749-fd09be7ccebf?auto=format&fit=crop&w=1200&q=80'
    }
  ];

  return (
    <div data-testid="experiences-page" className="min-h-screen pt-24 md:pt-32">
      {/* Page Header */}
      <section className="px-6 md:px-12 lg:px-24 py-16 md:py-24">
        <div className="max-w-[1400px] mx-auto">
          <p className="text-gold text-xs uppercase tracking-[0.4em] mb-5 font-manrope font-medium">
            What We Curate
          </p>
          <h1 className="font-playfair text-4xl md:text-5xl lg:text-6xl text-[#F5F5F0] mb-8 leading-[1.1]">
            Experiences That <span className="italic">Cannot Be Bought</span>
          </h1>
          <p className="text-[#A1A1AA] text-base md:text-lg max-w-2xl font-manrope font-light leading-relaxed">
            We don't display prices because what we offer isn't about cost — it's about access. 
            The right introduction. The perfect timing. The invisible orchestration that makes 
            extraordinary moments feel natural.
          </p>
        </div>
      </section>

      {/* Experience Sections */}
      {sections.map((section, sectionIndex) => (
        <section
          key={section.id}
          data-testid={`experience-section-${section.id}`}
          className={`py-20 md:py-28 px-6 md:px-12 lg:px-24 ${
            sectionIndex % 2 === 0 ? 'bg-obsidian' : 'bg-obsidian-paper'
          }`}
        >
          <div className="max-w-[1400px] mx-auto">
            {/* Section Header */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
              <div>
                <p className="text-gold text-xs uppercase tracking-[0.4em] mb-5 font-manrope font-medium">
                  {section.subtitle}
                </p>
                <h2 className="font-playfair text-3xl md:text-4xl text-[#F5F5F0] mb-6">
                  {section.title}
                </h2>
                <p className="text-[#A1A1AA] text-base leading-[1.8] font-manrope font-light">
                  {section.description}
                </p>
              </div>
              <div className="relative h-[300px] md:h-[400px] overflow-hidden border border-white/5">
                <img
                  src={section.mainImage}
                  alt={section.title}
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
              </div>
            </div>

            {/* Experience Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {section.experiences.map((exp, expIndex) => (
                <div
                  key={expIndex}
                  data-testid={`exp-card-${section.id}-${expIndex}`}
                  className="group relative h-[380px] overflow-hidden border border-white/5 hover:border-gold/20 transition-all duration-700"
                >
                  <img
                    src={exp.image}
                    alt={exp.title}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/10" />
                  <div className="absolute inset-0 flex flex-col justify-end p-6">
                    <h3 className="font-playfair text-xl text-[#F5F5F0] mb-2">
                      {exp.title}
                    </h3>
                    <p className="text-[#A1A1AA] text-sm font-manrope font-light leading-relaxed">
                      {exp.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* CTA Section */}
      <section className="py-28 md:py-36 px-6 md:px-12 lg:px-24 bg-obsidian">
        <div className="max-w-[1400px] mx-auto text-center">
          <p className="text-gold text-xs uppercase tracking-[0.4em] mb-8 font-manrope font-medium">
            Ready to Begin
          </p>
          <h2 className="font-playfair text-3xl md:text-4xl text-[#F5F5F0] mb-8">
            The best experiences aren't <span className="italic">advertised</span>
          </h2>
          <p className="text-[#A1A1AA] text-base md:text-lg max-w-xl mx-auto mb-12 font-manrope font-light">
            Tell us what you're looking for. We'll tell you what's possible.
          </p>
          <Link
            to="/contact"
            data-testid="experiences-cta"
            className="inline-flex items-center gap-3 bg-gold text-black px-10 py-4 text-xs uppercase tracking-[0.2em] font-bold hover:bg-gold-light transition-all duration-500 luxury-btn"
          >
            Request Private Access
            <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ExperiencesPage;
